import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import HighlightedExpression from './highlighted-expression';
type Condition = {
  expression: string;
  kind?: 'add' | 'remove';
};

// Set-based diff: order-stable for the current set, added conditions appended.
export const diffConditions = (current: string[], suggested: string[]): Condition[] => {
  const cur = new Set(current);
  const sug = new Set(suggested);
  const out: Condition[] = [];
  for (const c of current) {
    if (!sug.has(c)) {
      out.push({
        expression: c,
        kind: 'remove'
      });
    } else {
      out.push({
        expression: c
      });
    }
  }
  for (const s of suggested) {
    if (!cur.has(s)) out.push({
      expression: s,
      kind: 'add'
    });
  }
  return out;
};
type ConditionLineProps = {
  prefix: 'if' | 'or';
  expression: string;
  kind?: 'add' | 'remove';
};
const MARKER = {
  add: '+',
  remove: '−'
} as const;
const MARKER_COLOR = {
  add: 'text-success',
  remove: 'text-error'
} as const;
const ROW_BG = {
  add: 'bg-success-inverted/[0.06]',
  remove: 'bg-error-inverted/[0.06] line-through decoration-error/60'
} as const;
const ConditionLine = t0 => {
  const $ = _c(16);
  const {
    prefix,
    expression,
    kind
  } = t0;
  const t1 = kind && ROW_BG[kind];
  let t2;
  if ($[0] !== t1) {
    t2 = cx("flex items-start gap-2 px-2 py-1 -mx-2 rounded", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== prefix) {
    t3 = <span className="text-caption-3 text-tertiary uppercase tracking-wider min-w-[18px] mt-0.5">{prefix}</span>;
    $[2] = prefix;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = kind ? MARKER_COLOR[kind] : "text-transparent";
  let t5;
  if ($[4] !== t4) {
    t5 = cx("font-mono text-[12px] w-3 mt-0.5 select-none", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = kind ? MARKER[kind] : "\xB7";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <span aria-hidden={true} className={t5}>{t6}</span>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== expression) {
    t8 = <div className="flex-1 min-w-0 font-mono text-[12px] whitespace-pre-wrap break-all"><HighlightedExpression value={expression} /></div>;
    $[9] = expression;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className={t2}>{t3}{t7}{t8}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
};
export default ConditionLine;